import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@common/components/button';
import { combineClassNames } from '@utils/combineClassNames';
import { TRIAL_MODULE_KEYS, type TrialModuleKey } from '../../types';
import { TrialArrow, TrialBackArrow } from './trial-arrow';
import { TRIAL_CHECK_PATH, TRIAL_MODULE_ICON_PATHS, TrialStrokeIcon } from './trial-icons';
import type { PreviewValues } from './preview-step';

type Props = {
  values: PreviewValues;
  onChange: (values: PreviewValues) => void;
  onContinue: () => void;
  onBack: () => void;
};

export const ModulesStep = memo(({
  values, onChange, onContinue, onBack,
}: Props) => {
  const { t } = useTranslation();
  const allOn = values.modules.length === TRIAL_MODULE_KEYS.length;

  const toggle = (key: TrialModuleKey) => {
    const active = values.modules.includes(key);

    onChange({
      ...values,
      modules: active ? values.modules.filter((item) => item !== key) : [...values.modules, key],
    });
  };

  return (
    <div
      className={
        'FreeTrial__Glass FreeTrial__Glass--wide max-md:!tw-mb-0 max-md:!tw-w-full '
        + 'max-md:!tw-max-h-[calc(100dvh-96px)] max-md:!tw-overflow-y-auto '
        + 'max-md:!tw-rounded-b-none max-md:!tw-rounded-t-[28px] max-md:!tw-border-0'
      }
    >
      <div className="FreeTrial__Glass__Head">
        <p>{t('authentication:trial_setup_eyebrow', { current: 3, total: 3 })}</p>
        <h1>{t('authentication:trial_modules_title')}</h1>
      </div>
      <div className="FreeTrial__ModulesBar">
        <span>
          {t('authentication:trial_modules_selected', {
            count: values.modules.length,
            total: TRIAL_MODULE_KEYS.length,
          })}
        </span>
        <button
          type="button"
          className={combineClassNames('FreeTrial__SelectAll', { 'FreeTrial__SelectAll--on': allOn })}
          onClick={() => onChange({ ...values, modules: allOn ? [] : [...TRIAL_MODULE_KEYS] })}
        >
          <TrialStrokeIcon
            className="FreeTrial__SelectAll__Icon"
            d={TRIAL_CHECK_PATH}
            size={14}
            strokeWidth={2.8}
          />
          {t(allOn ? 'authentication:trial_modules_deselect_all' : 'authentication:trial_modules_select_all')}
        </button>
      </div>
      <div className="FreeTrial__ModuleGrid">
        {TRIAL_MODULE_KEYS.map((key) => {
          const active = values.modules.includes(key);

          return (
            <button
              key={key}
              type="button"
              className={combineClassNames('FreeTrial__Module', { 'FreeTrial__Module--on': active })}
              aria-pressed={active}
              onClick={() => toggle(key)}
            >
              <TrialStrokeIcon
                className="FreeTrial__Module__Icon"
                d={TRIAL_MODULE_ICON_PATHS[key]}
                size={18}
              />
              <span>{t(`authentication:trial_module_${key}`)}</span>
              {active && (
                <TrialStrokeIcon
                  className="FreeTrial__Module__Check"
                  d={TRIAL_CHECK_PATH}
                  size={14}
                  stroke="#2463eb"
                  strokeWidth={3.2}
                />
              )}
            </button>
          );
        })}
      </div>
      <div className="FreeTrial__Glass__Actions">
        <button type="button" className="FreeTrial__Back" onClick={onBack}>
          <TrialBackArrow />
          <span>{t('authentication:trial_setup_back')}</span>
        </button>
        <Button type="primary" onClick={onContinue}>
          <span className="FreeTrial__Cta">
            {t('authentication:trial_modules_build')}
            <TrialArrow />
          </span>
        </Button>
      </div>
    </div>
  );
});
